import React, { useState } from 'react';
import { supabase } from '../supabase';

/**
 * Buscador de cliente con dropdown filtrable.
 * Selección única — muestra chip con × para limpiar.
 * Si allowManual=true, muestra un input de texto libre cuando no hay cliente seleccionado.
 * Si allowCreate=true, muestra "+ Crear cliente" al pie del dropdown.
 *   onClienteCreado(nuevoCliente) se llama tras crear, para que el padre refresque su lista.
 */
export default function ClienteSearch({
  clientes = [],
  value,
  onChange,
  manualValue,
  onManualChange,
  allowManual = false,
  allowCreate = false,
  onClienteCreado,
  placeholder = 'Buscar cliente por nombre o DNI...',
}) {
  const [busqueda, setBusqueda] = useState('');
  const [open, setOpen] = useState(false);

  // Mini-modal crear cliente
  const [modalCrear, setModalCrear] = useState(false);
  const [formCrear, setFormCrear] = useState({ nombre: '', apellido: '', telefono: '', email: '' });
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState('');

  const clienteSeleccionado = clientes.find(c => c.id === value);

  const filtrados = clientes.filter(c => {
    const t = busqueda.toLowerCase();
    return !t || `${c.nombre} ${c.apellido}`.toLowerCase().includes(t) || (c.dni || '').includes(t);
  });

  function seleccionar(c) {
    onChange(c.id);
    setBusqueda('');
    setOpen(false);
  }

  function limpiar() {
    onChange('');
    setBusqueda('');
    if (onManualChange) onManualChange('');
  }

  function abrirCrear() {
    setOpen(false);
    setFormCrear({ nombre: busqueda, apellido: '', telefono: '', email: '' });
    setError('');
    setModalCrear(true);
  }

  async function guardarNuevoCliente() {
    if (!formCrear.nombre.trim()) { setError('El nombre es obligatorio'); return; }
    setGuardando(true);
    const { data: nuevo, error: err } = await supabase
      .from('clientes')
      .insert({ nombre: formCrear.nombre.trim(), apellido: formCrear.apellido.trim() || null, email: formCrear.email.trim() || null, estado: 'activo' })
      .select('id, nombre, apellido')
      .single();
    if (err || !nuevo) { setError('Error al crear el cliente'); setGuardando(false); return; }
    if (formCrear.telefono.trim()) {
      await supabase.from('clientes_telefonos').insert({ cliente_id: nuevo.id, tipo: 'Celular', numero: formCrear.telefono.trim() });
    }
    setGuardando(false);
    setModalCrear(false);
    onChange(nuevo.id);
    onClienteCreado?.(nuevo);
  }

  return (
    <div>
      {/* Chip del cliente seleccionado */}
      {clienteSeleccionado && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '3px 10px', borderRadius: 20, background: 'var(--blue-light)', border: '1px solid var(--blue)', color: 'var(--blue)', fontSize: 12 }}>
            {clienteSeleccionado.nombre} {clienteSeleccionado.apellido}
            <span onClick={limpiar} style={{ cursor: 'pointer', fontWeight: 700, marginLeft: 2 }}>×</span>
          </div>
        </div>
      )}

      {/* Input de búsqueda */}
      {!clienteSeleccionado && (
        <div style={{ position: 'relative' }}>
          <div style={{ display: 'flex', gap: 6 }}>
            <div style={{ position: 'relative', flex: 1 }}>
              <input
                className="form-input"
                placeholder={placeholder}
                value={busqueda}
                onChange={e => setBusqueda(e.target.value)}
                onFocus={() => setOpen(true)}
                onBlur={() => setTimeout(() => setOpen(false), 150)}
                autoComplete="off"
                spellCheck={false}
                style={{ width: '100%' }}
              />
              {open && (
                <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 8, zIndex: 200, maxHeight: 220, overflowY: 'auto', marginTop: 4, boxShadow: '0 4px 16px rgba(0,0,0,0.3)' }}>
                  {filtrados.length === 0 && (
                    <div style={{ padding: '10px 14px', color: 'var(--text4)', fontSize: 13 }}>Sin resultados</div>
                  )}
                  {filtrados.map(c => (
                    <div
                      key={c.id}
                      onMouseDown={() => seleccionar(c)}
                      style={{ padding: '8px 14px', cursor: 'pointer', fontSize: 13, color: 'var(--text2)' }}
                      onMouseEnter={e => e.currentTarget.style.background = 'var(--surface2)'}
                      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                    >
                      {c.nombre} {c.apellido}
                      {c.dni && <span style={{ fontSize: 11, color: 'var(--text4)', marginLeft: 8 }}>{c.dni}</span>}
                    </div>
                  ))}
                </div>
              )}
            </div>
            {allowCreate && (
              <button
                type="button"
                className="btn btn-ghost"
                onClick={abrirCrear}
                title="Crear nuevo cliente"
                style={{ flexShrink: 0, fontSize: 18, lineHeight: 1, padding: '0 12px' }}
              >
                +
              </button>
            )}
          </div>
        </div>
      )}

      {/* Campo manual */}
      {allowManual && !clienteSeleccionado && (
        <div style={{ marginTop: 8 }}>
          <label className="form-label" style={{ fontSize: 11 }}>
            o ingresá el nombre manualmente <span style={{ color: 'var(--text4)', fontWeight: 400 }}>(si no tiene ficha)</span>
          </label>
          <input
            className="form-input"
            value={manualValue || ''}
            onChange={e => onManualChange?.(e.target.value)}
            placeholder="Nombre sin crear ficha"
          />
        </div>
      )}

      {/* Mini-modal crear cliente */}
      {modalCrear && (
        <div className="modal-overlay" onMouseDown={e => e.target === e.currentTarget && setModalCrear(false)} style={{ zIndex: 9999 }}>
          <div className="modal" style={{ width: 420 }}>
            <div className="modal-header">
              <div className="modal-title">Nuevo cliente</div>
              <button className="modal-close" onClick={() => setModalCrear(false)}>✕</button>
            </div>
            <div className="modal-body">
              <div className="form-grid">
                <div className="form-group">
                  <label className="form-label">Nombre *</label>
                  <input className="form-input" value={formCrear.nombre} onChange={e => setFormCrear(f => ({ ...f, nombre: e.target.value }))} placeholder="Nombre" autoFocus />
                </div>
                <div className="form-group">
                  <label className="form-label">Apellido</label>
                  <input className="form-input" value={formCrear.apellido} onChange={e => setFormCrear(f => ({ ...f, apellido: e.target.value }))} placeholder="Apellido" />
                </div>
                <div className="form-group">
                  <label className="form-label">Teléfono</label>
                  <input className="form-input" value={formCrear.telefono} onChange={e => setFormCrear(f => ({ ...f, telefono: e.target.value }))} placeholder="Celular" />
                </div>
                <div className="form-group">
                  <label className="form-label">Email</label>
                  <input className="form-input" type="email" value={formCrear.email} onChange={e => setFormCrear(f => ({ ...f, email: e.target.value }))} placeholder="email@ejemplo.com" />
                </div>
              </div>
              {error && <div style={{ color: 'var(--red)', fontSize: 12, marginTop: 8 }}>{error}</div>}
              <div style={{ fontSize: 11, color: 'var(--text4)', marginTop: 10 }}>
                Podés completar el resto de la ficha desde la sección Clientes.
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={() => setModalCrear(false)}>Cancelar</button>
              <button className="btn btn-primary" onClick={guardarNuevoCliente} disabled={guardando}>
                {guardando ? 'Guardando...' : 'Crear y seleccionar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
